import React, { useState, useEffect } from 'react'
import { Text } from '../../constants/TextConstants'
import { useNavigate } from 'react-router-dom'
import { epagAPI } from '../../API/epagAPI'
import { LoadingSpin } from '../LoadingSpin'
import { useGetToken } from '../../hooks/useGetToken'
import { BqButton, BqDialog, BqIcon } from '@beeq/react'
import { StyledWarningFooter, StyledWarningTitle, PageWrapper, StyledPageActionWrapper, TableWrapper } from './styles'
import { useSave } from '../../Context/saveContext'
import ViewRole from './ViewRole'
import CreateRole from '../Modals/CreateRole'
import { useBoolean } from '../../hooks/useBoolean'
import Pagination from '../Pagination'
import { isAdminRole, isSystemRole } from '../../utils/utils'
import { Toast } from '../Toast'
import { useSort } from '../../hooks/useSort'
import { sortRoles, systemRolesOrder } from './Sorting/sorting'
import { TableHeader } from './TableHeader/TableHeader'
import { TableRows } from './TableRows/TableRows'
import RolesPermissionsTabs from '../RolesPermissionsTabs'
import { NoDataFound } from '../Profiles/NoDataFound'

// in px
export const columnAndRowsWidth = {
  roles: '912',
  users: '96',
  actions: '75'
}

export const Roles = () => {
  const { token } = useGetToken()
  const navigate = useNavigate()

  const [isCreateRoleOpen, { on: openCreateRole, off: closeCreateRole }] = useBoolean()

  const [isEditRoleWarningOpen, { on: openEditRoleWarningOpen, off: closeEditRoleWarningOpen }] = useBoolean()

  const [isViewRoleOpen, { on: openViewRole, off: closeViewRole }] = useBoolean()

  const [isDeleteRoleOpen, { on: openDeleteRole, off: closeDeleteRole }] = useBoolean()
  const [toastMessage, setToastMessage] = useState('')

  const [selectedRole, setSelectedRole] = useState(null)

  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const [roles, setRoles] = useState([])
  const [sortState, toggleSort] = useSort({ roles: { order: 'asc' } })

  const [permissionList, setPermissionList] = useState([])

  const [actualPage, setActualPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const onPageSizeChange = (pageSize) => {
    setActualPage(1)
    setItemsPerPage(pageSize)
  }

  const [displayedRoles, setDisplayedRoles] = useState([])

  useEffect(() => {
    if (roles.length && Object.keys(sortState).length) {
      setRoles(sortRoles(roles, sortState))

      const startIndex = (actualPage - 1) * itemsPerPage
      const endIndex = startIndex + itemsPerPage

      setDisplayedRoles(roles.slice(startIndex, endIndex))
    }
  }, [sortState, roles, actualPage, itemsPerPage])

  const { userProfile, updateProfile, isLoading: isUseSaveLoading } = useSave()

  const displayActionButtons = (roleName) => isAdminRole(userProfile.userRole) || !isSystemRole(roleName)

  const closeEditRoleWarningModal = () => {
    closeCreateRole()
    closeEditRoleWarningOpen()
    setSelectedRole(null)
  }

  const closeViewRoleModal = () => {
    closeViewRole()
    setSelectedRole(null)
  }

  const closeDeleteRoleModal = () => {
    closeDeleteRole()
    setSelectedRole(null)
  }

  const getRoles = () => {
    setIsLoadingRoles(true)
    epagAPI
      .GetRoles()
      .then((result) => {
        if (result) {
          // adds priority for system (a.k.a default) roles
          const roles = result.map((role) => {
            if (systemRolesOrder[role.name]) {
              role.systemRoleOrder = systemRolesOrder[role.name]
            }
            return role
          })
          setIsLoadingRoles(false)
          setRoles(roles)
        } else {
          navigate('/NotFound', { replace: true })
        }
      })
      .catch(() => {
        navigate('/Forbidden', { replace: true })
      })
  }

  const getPermissions = () => {
    epagAPI.GetPermissions(token).then((result) => {
      if (result) {
        setPermissionList(result)
      } else {
        navigate('/NotFound', { replace: true })
      }
    })
  }

  const getRoleById = (idRole) => {
    return epagAPI
      .GetRole(idRole)
      .then((result) => {
        if (result) {
          setSelectedRole(result)
          return result
        } else {
          navigate('/NotFound', { replace: true })
        }
      })
      .catch(() => {
        navigate('/Forbidden', { replace: true })
      })
  }

  useEffect(() => {
    if (token) {
      getRoles()
      getPermissions()
    }
  }, [token])

  const onClickEditRole = (roleId) => {
    getRoleById(roleId).then((result) => {
      if (!result) return

      openCreateRole()

      // Shows a warning if the user is updating their own role
      if (result.name === userProfile.userRole) {
        openEditRoleWarningOpen()
      }
    })
  }

  const onClickDeleteRole = (idRole) => {
    getRoleById(idRole).then((result) => {
      if (!result) return

      openDeleteRole()
    })
  }

  const onClickViewRole = (roleId) => {
    getRoleById(roleId).then((result) => {
      if (!result) return

      openViewRole()
    })
  }

  const deleteRole = () => {
    epagAPI
      .DeleteRole(selectedRole._id)
      .then((response) => {
        if (response.deletedCount > 0) {
          setToastMessage(Text.RoleDeletedSuccess)
          getRoles()
          updateProfile()
        }
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        closeDeleteRoleModal()
      })
  }

  const saveChanges = (values) => {
    if (!values._id) {
      epagAPI
        .CreateRole(values, token)
        .then((response) => {
          if (response) {
            setToastMessage(Text.RoleCreatedSuccess)
          }
          getRoles()
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(closeCreateRole)
    } else {
      return epagAPI
        .UpdateRole(values, token)
        .then((response) => {
          if (response) {
            setToastMessage(Text.RoleModifiedSuccess)
          }
          updateProfile()
          getRoles()
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(closeCreateRole)
    }
  }

  if (isUseSaveLoading) {
    return <LoadingSpin loadingText={Text.GenericLoading} />
  }

  return (
    <PageWrapper>
      <Toast
        open={Boolean(toastMessage)}
        type={'success'}
        message={toastMessage}
        onBqHide={() => setToastMessage('')}
      />

      <RolesPermissionsTabs />

      <StyledPageActionWrapper>
        <BqButton appearance='link' onBqClick={openCreateRole}>
          <BqIcon name='plus-square' weight='bold' slot='prefix' />
          <span className='semibold'>{Text.CreateRole}</span>
        </BqButton>
      </StyledPageActionWrapper>

      <TableWrapper>
        <TableHeader sortState={sortState} toggleSort={toggleSort} />
        {displayedRoles.length || isLoadingRoles ? (
          <TableRows
            displayedRoles={displayedRoles}
            disableActions={displayActionButtons}
            onViewRole={onClickViewRole}
            onEditRole={onClickEditRole}
            onDeleteRole={onClickDeleteRole}
            isLoading={isLoadingRoles}
          />
        ) : (
          <NoDataFound />
        )}
      </TableWrapper>
      {(displayedRoles.length || isLoadingRoles) && (
        <Pagination
          currentPage={actualPage}
          totalItems={roles.length}
          defaultPageSize={itemsPerPage}
          onPageSizeChange={onPageSizeChange}
          onPageChange={setActualPage}
          pageSizeControlSuffix={Text.Roles}
        />
      )}

      <ViewRole open={isViewRoleOpen} onClose={closeViewRoleModal} role={selectedRole} permissions={permissionList} />
      <BqDialog open={isDeleteRoleOpen} onBqClose={closeDeleteRoleModal} onBqCancel={closeDeleteRoleModal} size='large'>
        <StyledWarningTitle className='h5 bold' slot='title'>
          <BqIcon name='info' weight='bold' size={30} />
          {Text.DeleteRoleTitle.replace('{{name}}', selectedRole?.name)}
        </StyledWarningTitle>
        <span>{Text.DeleteRoleDescription.replace('{{count}}', selectedRole?.assignedUsersCount)}</span>
        <div>{Text.DeleteRoleDescription2}</div>
        <StyledWarningFooter slot='footer'>
          <BqButton appearance='link' onBqClick={closeDeleteRoleModal}>
            {Text.Cancel}
          </BqButton>
          <BqButton onBqClick={deleteRole}>{Text.Delete}</BqButton>
        </StyledWarningFooter>
      </BqDialog>
      <BqDialog
        open={isEditRoleWarningOpen}
        onBqClose={closeEditRoleWarningModal}
        onBqCancel={closeEditRoleWarningModal}>
        <StyledWarningTitle className='h5 bold' slot='title'>
          <BqIcon name='info' weight='bold' size={30} />
          {Text.EditYourOwnRoleTitle}
        </StyledWarningTitle>
        <span>{Text.EditYourOwnRoleDescription.replace('{{name}}', selectedRole?.name)}</span>
        <StyledWarningFooter slot='footer'>
          <BqButton appearance='link' onBqClick={closeEditRoleWarningModal}>
            {Text.Cancel}
          </BqButton>
          <BqButton onBqClick={closeEditRoleWarningOpen}>{Text.EditYourOwnRoleContinue}</BqButton>
        </StyledWarningFooter>
      </BqDialog>

      <CreateRole
        open={isCreateRoleOpen && !isEditRoleWarningOpen}
        role={selectedRole}
        permissions={permissionList}
        roles={roles}
        onSubmit={saveChanges}
        onClose={() => {
          closeCreateRole()
          setSelectedRole(null)
        }}
      />
    </PageWrapper>
  )
}
